import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  GetPropsCommonOptions,
  GetToggleButtonPropsOptions,
  useCombobox,
  UseComboboxGetComboboxPropsOptions,
} from 'downshift';

import { InputField } from '../components/InputField';
import { getDownshiftLabelProps } from '../components/downshiftLabelProps';
import { ToggleButton } from '../components/ToggleButton';
import { InputFieldClearButton } from '../components/InputFieldClearButton';
import { Box } from '../../Box';
import { Item, Items } from './types';
import { MultiselectFieldBody } from '../Multiselect/components/MultiselectFieldBody';
import { SelectMenu } from './components/SelectMenu';
import { SelectedItem } from './renderers/SelectedItemRenderer';

const Boxed = styled(Box)`
  flex: 1;
  flex-flow: wrap;
`;

const SelectMenuWrapper = styled.span`
  position: relative;
`;

const searchFilter =
  (inputValue: string) =>
  ({ title }: { title: string }): boolean => {
    const lowerCasedInputValue = inputValue.toLowerCase();

    return !inputValue || title.toLowerCase().includes(lowerCasedInputValue);
  };

type SelectSearchBodyProps = {
  getComboboxProps: (
    options?: UseComboboxGetComboboxPropsOptions,
    otherOptions?: GetPropsCommonOptions
  ) => unknown;
  getInputProps: ReturnType<typeof useCombobox>['getInputProps'];
  openMenu: (cb?: () => void) => void;
  placeholder: string;
  isOpen: boolean;
  disabled: boolean;
  selectedItem: ReturnType<typeof useCombobox>['selectedItem'];
  showClearButton?: boolean;
  selectItem: ReturnType<typeof useCombobox>['selectItem'];
  getToggleButtonProps: (options?: GetToggleButtonPropsOptions) => unknown;
  name?: string;
  labelProps: Record<string, string | undefined>;
};
const SelectSearchBody = ({
  getComboboxProps,
  getInputProps,
  openMenu,
  placeholder,
  isOpen,
  disabled,
  selectedItem,
  showClearButton,
  selectItem,
  getToggleButtonProps,
  name,
  labelProps,
}: SelectSearchBodyProps) => (
  <Box display="flex" justifyContent="space-between" width="100%">
    <Boxed display="flex">
      {!!selectedItem && (
        <SelectedItem
          item={selectedItem as Item}
          isOpen={isOpen}
          placeholder={placeholder}
          openMenu={openMenu}
          disabled={disabled}
        />
      )}

      <MultiselectFieldBody
        getComboboxProps={getComboboxProps}
        getInputProps={getInputProps}
        openMenu={openMenu}
        getDropdownProps={() => null}
        placeholder={placeholder}
        isOpen={isOpen}
        disabled={disabled}
        labelProps={labelProps}
      />
    </Boxed>

    <Box display="flex" justify-content="center">
      {selectedItem && showClearButton ? (
        <InputFieldClearButton
          onClick={() => {
            selectItem(null);
          }}
          disabled={disabled}
          align="center"
        />
      ) : null}

      <ToggleButton
        getToggleButtonProps={getToggleButtonProps}
        isOpen={isOpen}
        disabled={disabled}
        align="center"
        name={name}
      />
    </Box>
  </Box>
);

type RawSelectSearchProps = {
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  onChange: (value: Item | null) => void;
  items: Items;
  initialValue: Item['value'];
  emptyMessage: string;
  name?: string;
  maxHeight?: string;
  maxTextWidth?: string;
  showClearButton?: boolean;
  value?: Item['value'];
  onInputChange?: (value: Item['value'] | null) => void;
  labelId?: string;
};

function RawSelectSearch({
  placeholder,
  hasError,
  onChange,
  disabled,
  initialValue,
  name,
  maxHeight,
  showClearButton,
  items,
  emptyMessage,
  onInputChange,
  labelId,
}: RawSelectSearchProps) {
  const labelProps = getDownshiftLabelProps(labelId, placeholder);
  const anchorEl = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const hasInitializedRef = useRef(false);

  const initialSelectedItem = items.find(item => item.value === initialValue);

  // Create filtered items for search
  const filteredItems = items.filter(searchFilter(inputValue));

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    openMenu,
    selectItem,
    selectedItem,
    getInputProps,
    getComboboxProps,
  } = useCombobox({
    ...(labelId && { labelId }),
    items: filteredItems,
    inputValue,
    onInputValueChange({ inputValue: newInputValue, type }) {
      const isSelectionEvent =
        type === useCombobox.stateChangeTypes.ItemClick ||
        type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
        type === useCombobox.stateChangeTypes.FunctionSelectItem;

      if (isSelectionEvent) {
        setInputValue('');
        if (onInputChange) onInputChange(null);
        return;
      }

      setInputValue(newInputValue ?? '');
      if (onInputChange) onInputChange(newInputValue || null);
    },
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      // Filter out Group entities since onChange expects Item | null
      if (newSelectedItem?.entityType !== 'group') {
        onChange(newSelectedItem || null);
      }
    },
    itemToString(item) {
      return item ? item.title : '';
    },
    initialSelectedItem,
  });

  // Create items for dropdown - include selected item if it's not already in filtered items
  const dropdownItems =
    selectedItem &&
    !filteredItems.find(item => item.value === selectedItem.value)
      ? [...filteredItems, selectedItem]
      : filteredItems;

  // Handle async data loading - update selectedItem when items change
  useEffect(() => {
    if (initialSelectedItem && !hasInitializedRef.current) {
      selectItem(initialSelectedItem);
      hasInitializedRef.current = true;
    }
  }, [items, selectItem, initialSelectedItem]);

  return (
    <>
      <InputField
        hasError={hasError}
        disabled={disabled}
        ref={anchorEl}
        paddingRight="6px"
        isFocused={isOpen}
      >
        <SelectSearchBody
          getComboboxProps={getComboboxProps}
          getInputProps={getInputProps}
          openMenu={openMenu}
          placeholder={placeholder ?? ''}
          isOpen={isOpen}
          disabled={disabled ?? false}
          selectedItem={selectedItem}
          showClearButton={showClearButton}
          selectItem={selectItem}
          getToggleButtonProps={getToggleButtonProps}
          name={name}
          labelProps={labelProps}
        />
      </InputField>

      <SelectMenuWrapper {...getMenuProps(labelProps)}>
        {isOpen && (
          <SelectMenu
            isOpen={isOpen}
            anchorEl={anchorEl}
            emptyMessage={emptyMessage}
            maxHeight={maxHeight}
            items={dropdownItems}
            getItemProps={getItemProps}
          />
        )}
      </SelectMenuWrapper>
    </>
  );
}

export { RawSelectSearch };
